import { DateTime } from 'luxon'
import React, { MouseEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { PrivateCompanyIndicator } from '@components/PrivateCompanyIndicator'
import ProfileAlertButton from '@modules/alerts/components/ProfileAlertButton'
import { ProfileAlert } from '@modules/alerts/types'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusEcommerceUser } from '@modules/auth/types'
import ContactAttendeesPopover from '@modules/chat/components/ContactAttendeesPopover'
import ContactedBubble from '@modules/chat/components/ContactedBubble'
import { ChatType } from '@modules/chat/types'
import { useChatContext } from '@modules/chat/useChatContext'
import {
  ProfileEventInfo,
  ProfileEventName,
  ProfileName,
  ProfileTitle,
  ProfileTitleButtons,
  ProfileTitleInner,
  ProfileTitleWrapper,
  StyledHeaderProfileLogo,
} from '@modules/profile/index.styles'
import { getCompanyId } from '@modules/search/components/ResultsSection/util/ResultProfile.util'
import { AnyResultProfile, ProfileType } from '@modules/search/types'
import ShortlistProfileButton from '@modules/shortlist/components/ShortlistProfileButton'
import { Box, Button } from '@mui/material'
import Typography from '@mui/material/Typography'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'
import { dateTimeFormats } from '@theme/variables'

import useAttendees from './hooks/useAttendees'

export type ProfileHeaderProps = {
  profile: AnyResultProfile
  profileName: {
    title?: string
    firstName?: string
    lastName?: string
  }
  eventDate?: string
  eventName?: string
  profileJobTitle?: string
  profileDivision?: string
  profileType: ProfileType
  profileAlert?: ProfileAlert
  isPrivate?: boolean
}

const EventInfo = (isDelegate: boolean, eventName: string | undefined, eventDate = '') => {
  return (
    (eventDate || eventName) && (
      <ProfileEventInfo variant="body1" $isDelegate={isDelegate} data-testid="profile_event_info">
        {DateTime.fromISO(eventDate).toFormat(dateTimeFormats.eventDateFormat)}
        {eventName && <ProfileEventName data-testid="profile_event_name">{eventName}</ProfileEventName>}
      </ProfileEventInfo>
    )
  )
}

const ProfileHeader = (props: ProfileHeaderProps) => {
  const {
    profile,
    profileName: { title, firstName, lastName } = {},
    eventDate = '',
    eventName,
    profileJobTitle,
    profileDivision,
    profileType,
    profileAlert,
    isPrivate,
  } = props
  const withProfileLogo = [ProfileType.company, ProfileType.delegate].includes(profileType)
  const isDelegateProfile = profileType === ProfileType.delegate
  const { t } = useTranslation()
  const { user } = useAuthContext()
  const isEcommerce = isP1PlusEcommerceUser(user?.subscriptionLevel || 0)
  const { hasAccessToChat, hasAccessToAlerts } = useFeatureFlags()
  const { openChatModal } = useChatContext()
  const { hasContactedWithCompany, hasContactedDelegate } = useAttendees({
    companyId: getCompanyId(profile),
    delegateId: profile.id,
  })
  const [contactAttendeesPopoverData, setContactAttendeesPopoverData] = useState<
    Nullable<{
      anchor?: Nullable<HTMLElement>
      profile?: AnyResultProfile
    }>
  >(null)

  const shouldShowContactedBubble = useMemo(
    () => (isDelegateProfile ? hasContactedDelegate : hasContactedWithCompany),
    [hasContactedWithCompany, hasContactedDelegate],
  )

  const onContactClick = (e: MouseEvent<HTMLElement>) => {
    if (profile['@type'] === ProfileType.delegate && profile.latest.id !== user?.id) {
      return openChatModal({ delegates: [profile.latest], companyId: profile.latest.companyId, chatType: ChatType.DIRECT })
    }

    if (e?.currentTarget) {
      setContactAttendeesPopoverData({ anchor: e?.currentTarget, profile })
      return null
    }

    return setContactAttendeesPopoverData(null)
  }

  return (
    <ProfileTitleWrapper>
      <ProfileTitleInner fullWidth={isDelegateProfile}>
        <ProfileTitle>
          <>
            {withProfileLogo && (
              <StyledHeaderProfileLogo
                profileData={profile.latest}
                profileType={profileType}
                width={isDelegateProfile ? 90 : 65}
                defaultLogoSize={43}
                circleView
              />
            )}
            <div>
              <ProfileName variant="h2">
                {title} {firstName} {lastName}
                {isPrivate && (
                  <Box sx={{ marginLeft: '10px', display: 'inline' }}>
                    <PrivateCompanyIndicator />
                  </Box>
                )}
              </ProfileName>
              {profileJobTitle && <Typography variant="h2">{profileJobTitle}</Typography>}
              {profileDivision && <Typography variant="body1">{profileDivision}</Typography>}
              {!isDelegateProfile && EventInfo(isDelegateProfile, eventName, eventDate)}
            </div>
          </>
        </ProfileTitle>
        {isDelegateProfile && EventInfo(isDelegateProfile, eventName, eventDate)}
      </ProfileTitleInner>
      <ProfileTitleButtons>
        {hasAccessToAlerts && (
          <ProfileAlertButton
            profile={profile}
            withLastViewedUpdate
            savedProfileAlert={profileAlert}
            isPrivate={!profile.hasPublicProfile}
          />
        )}
        <ShortlistProfileButton profile={profile.latest} events={profile.events} />
        {profile.latest.id !== user?.id && hasAccessToChat && profile.hasPublicProfile && !isEcommerce && (
          <ContactedBubble hasContacted={shouldShowContactedBubble}>
            <Button sx={{ marginLeft: '10px' }} variant="contained" data-testid="contact_btn" onClick={onContactClick}>
              {t(translationKeys.CONTACT_BUTTON)}
            </Button>
          </ContactedBubble>
        )}
      </ProfileTitleButtons>

      {!!contactAttendeesPopoverData?.profile && (
        <ContactAttendeesPopover
          anchor={contactAttendeesPopoverData?.anchor}
          profile={contactAttendeesPopoverData?.profile}
          close={() => setContactAttendeesPopoverData(null)}
          openChatModal={openChatModal}
        />
      )}
    </ProfileTitleWrapper>
  )
}

export default ProfileHeader
